
import { Component, EventEmitter, Input, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ReactiveFormsModule, FormGroup, FormControl, Validators, FormArray, AbstractControl, ValidationErrors } from '@angular/forms';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';
import { InputFieldComponent } from '../../../../components/form/input-field/input-field.component';
import { SelectFieldComponent } from '../../../../components/form/select-field/select-field.component';
import { AlertInfo } from '../../../../models/alert-info';
import { SavedDataInfo } from '../../../../models/form/saved-data-info';
import { AlertNotificationService } from '../../../../services/alert-notification.service';
import { ProposalsService } from '../../../../services/api/proposals.service';
import { FormService } from '../../../../services/form.service';
import { ValidatorService } from '../../../../services/validator.service';
import { ProposalsPaymentConditionComponent } from "../proposals-payment-condition/proposals-payment-condition.component";
import { OptionService } from 'src/app/services/api/option.service';
import { BankAccountsService } from 'src/app/services/api/bank-accounts.service';
import { HttpParams } from '@angular/common/http';
import { CommonService } from 'src/app/services/common.service';

@Component({
  selector: 'proposals-data',
  imports: [NgbAlert, InputFieldComponent, SelectFieldComponent, ReactiveFormsModule, ProposalsPaymentConditionComponent],
  templateUrl: './proposals-data.component.html',
  styleUrl: './proposals-data.component.scss'
})
export class ProposalsDataComponent {

  @Input() proposal?: any;
  @Input() readonlyProposalData!: boolean;
  @Output() onSavedData = new EventEmitter<SavedDataInfo>();
  @Output() targetConversionChange = new EventEmitter<{ rate: number | null; currencyLabel: string | null }>();

  form!: FormGroup;
  @ViewChild(ProposalsPaymentConditionComponent) paymentConditionsComp!: ProposalsPaymentConditionComponent;
  paymentConditionsErrors: string[] = [];
  paymentConditionsInvalid: boolean = false;

  alertInfo: AlertInfo = { show: false, type: '', message: '' };
  proposalsSteps = [];

  proposalsModels = [ ];

  paymentCompany = [ ];

  private readonly PAYMENT_COMPANY_APPLICANT_KEY = 'APPLICANT';

  currenciesOptions: any[] = [];

  paymentMethods = [
    { key: 'Transfer Wise', value: 'Transfer Wise' },
    { key: 'Emoney', value: 'Emoney' },
    { key: 'Revolut', value: 'Revolut' }
  ];
  constructor(
    private validatorService: ValidatorService,
    private formService: FormService,
    private alertNotificationService: AlertNotificationService,
    private proposalsService: ProposalsService,
    private optionsService: OptionService,
    private banckAccountsService: BankAccountsService,
    private commonService: CommonService
  ) {
  }

  ngOnInit(): void {
    const atLeastOnePaymentCondition = (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!Array.isArray(value) || value.length === 0) {
        return { 'requireAtLeastOne': true };
      }
      return null;
    };

    this.form = new FormGroup({
      date: new FormControl('',),
      category: new FormControl('',),
      projectTime: new FormControl('', Validators.required),
      proposalSteps: new FormControl('', Validators.required),
      proposalModel: new FormControl('', Validators.required),
      placeOfSign: new FormControl('',),
      manufacturerCompanyName: new FormControl(''),
      manufacturerAddress: new FormControl(''),
      manufacturerContactPerson: new FormControl(''),
      manufacturerEmailAddress: new FormControl(''),
      manufacturerVatNumber: new FormControl(''),
      paymentCompany: new FormControl('', Validators.required),
      proposalPaymentConditions: new FormControl([], [Validators.required, atLeastOnePaymentCondition]),
      paymentMethod: new FormControl([], Validators.required),
      sentOn: new FormControl('',),
      dateSentOn: new FormControl({value:'', disabled: true},),
      reminderDate: new FormControl('',),
      checked: new FormControl('',),
      dateChecked: new FormControl('',),
      targetCurrencyId: new FormControl('', Validators.required),
      targetExchangeRate: new FormControl('', Validators.required),
    });

    this.form.get('proposalModel')?.valueChanges.subscribe(value => {
      if (value) {
        this.updateFormValidators(value);

        if (value === 'SAME_MAN_APPL') {
          const paymentCompanyControl = this.form.get('paymentCompany');
          if (paymentCompanyControl?.value !== this.PAYMENT_COMPANY_APPLICANT_KEY) {
            paymentCompanyControl?.setValue(this.PAYMENT_COMPANY_APPLICANT_KEY);
          }
        }
      }
    });

    this.form.get('targetCurrencyId')?.valueChanges.subscribe(value => {
      const selectedOption = this.currenciesOptions.find(option => String(option.key) === String(value));
      if (!selectedOption?.proposalBaseCurrency) {
        this.form.get('targetExchangeRate')?.setValue('', { emitEvent: false });
      }
      this.applyBaseCurrencyExchangeRate(value);
      this.emitTargetConversion();
    });

    this.form.get('targetExchangeRate')?.valueChanges.subscribe(() => {
      this.emitTargetConversion();
    });

    if (this.proposal?.data) {
      setTimeout(() => {
        const proposalStepsValue = this.findKeyByValue(this.proposalsSteps, this.proposal?.data?.proposalSteps);
        const proposalModelValue = this.findKeyByValue(this.proposalsModels, this.proposal?.data?.proposalModel);
        const paymentCompanyValue = this.findKeyByValue(this.paymentCompany, this.proposal?.data?.paymentCompany);
        const paymentMethodValue = this.findKeyByValue(this.paymentMethods, this.proposal?.data?.paymentMethod);

        if (proposalStepsValue) this.form.get('proposalSteps')?.setValue(proposalStepsValue);
        if (proposalModelValue) this.form.get('proposalModel')?.setValue(proposalModelValue);
        if (paymentCompanyValue) this.form.get('paymentCompany')?.setValue(paymentCompanyValue);
        if (paymentMethodValue) this.form.get('paymentMethod')?.setValue(paymentMethodValue);
      }, 0);
    }

    const params = new HttpParams().set('size', '9999');
    this.proposalsService.listSettingsProposalSteps().subscribe((res: any) => {
      this.proposalsSteps = res.data.proposalSteps?.map((step: any) => ({
        key: step.proposalStepId,
        value: step.name
      }));

      if (this.proposal?.data?.proposalSteps) {
        const proposalStepsValue = this.findKeyByValue(this.proposalsSteps, this.proposal?.data?.proposalSteps);
        this.form.get('proposalSteps')?.setValue(proposalStepsValue);
      }
    });

    this.optionsService.proposalModels().subscribe((res: any) => {
      this.proposalsModels = res.data.options

      if (this.proposal?.data?.proposalModel) {
        const proposalModelValue = this.findKeyByValue(this.proposalsModels, this.proposal?.data?.proposalModel);
        this.form.get('proposalModel')?.setValue(proposalModelValue);
      }
    });

    this.optionsService.proposalPaymentCompany().subscribe((res: any) => {
      this.paymentCompany = res.data.options

      if (this.proposal?.data?.paymentCompany) {
        const paymentCompanyValue = this.findKeyByValue(this.paymentCompany, this.proposal?.data?.paymentCompany);
        this.form.get('paymentCompany')?.setValue(paymentCompanyValue);
      }
    });

    this.banckAccountsService.list({params}).subscribe((res: any) => {
      const bankAccounts = res.data.bankAccounts || [];
      this.paymentMethods = bankAccounts.map((account: any) => ({
        key: account.bankAccountId,
        value: account.bankName
      }));

      if (this.proposal?.data?.paymentMethod) {
        const paymentMethodValue = this.findKeyByValue(this.paymentMethods, this.proposal?.data?.paymentMethod);
        this.form.get('paymentMethod')?.setValue(paymentMethodValue);
      }
    });

    this.proposalsService.getCurrencies().subscribe((res: any) => {
      this.currenciesOptions = (res?.data?.currencies || []).map((currency: any) => ({
        key: currency.currencyId,
        value: currency.name,
        code: currency.code,
        proposalBaseCurrency: currency.proposalBaseCurrency
      }));
      this.applyBaseCurrencyExchangeRate(this.form.get('targetCurrencyId')?.value);
      this.emitTargetConversion();
    });
  }

  private applyBaseCurrencyExchangeRate(currencyId: any): void {
    const targetExchangeRateControl = this.form.get('targetExchangeRate');
    const selectedOption = this.currenciesOptions.find(option => String(option.key) === String(currencyId));

    if (selectedOption?.proposalBaseCurrency) {
      targetExchangeRateControl?.setValue(1, { emitEvent: false });
      targetExchangeRateControl?.disable({ emitEvent: false });
    } else {
      targetExchangeRateControl?.enable({ emitEvent: false });
    }
  }

  private emitTargetConversion(): void {
    const rate = Number(this.form.get('targetExchangeRate')?.value) || null;
    const currencyId = this.form.get('targetCurrencyId')?.value;
    const currencyLabel = currencyId
      ? this.currenciesOptions.find(option => String(option.key) === String(currencyId))?.code || null
      : null;

    this.targetConversionChange.emit({ rate, currencyLabel });
  }

  private findKeyByValue(options: any[], valueToFind: string): string {
    if (!valueToFind) return '';
    const matchingOption = options.find(
      option => option.key === valueToFind || option.value === valueToFind
    );

    return matchingOption ? matchingOption.key : valueToFind;
  }

  private updateFormValidators(proposalModelValue: string): void {
    const placeOfSignControl = this.form.get('placeOfSign');
    const manufacturerCompanyNameControl = this.form.get('manufacturerCompanyName');
    const manufacturerAddressControl = this.form.get('manufacturerAddress');
    const manufacturerContactPersonControl = this.form.get('manufacturerContactPerson');
    const manufacturerEmailAddressControl = this.form.get('manufacturerEmailAddress');
    const manufacturerVatNumberControl = this.form.get('manufacturerVatNumber');

    placeOfSignControl?.clearValidators();
    manufacturerCompanyNameControl?.clearValidators();
    manufacturerAddressControl?.clearValidators();
    manufacturerContactPersonControl?.clearValidators();
    manufacturerEmailAddressControl?.clearValidators();
    manufacturerVatNumberControl?.clearValidators();

    if (proposalModelValue === 'SAME_MAN_APPL') {
      placeOfSignControl?.setValidators([Validators.required]);
    } else if (proposalModelValue === 'DIFF_MAN_APPL') {
      placeOfSignControl?.setValidators([Validators.required]);
      manufacturerCompanyNameControl?.setValidators([Validators.required]);
      manufacturerAddressControl?.setValidators([Validators.required]);
      manufacturerContactPersonControl?.setValidators([Validators.required]);
      manufacturerEmailAddressControl?.setValidators([Validators.required, Validators.email]);
      manufacturerVatNumberControl?.setValidators([Validators.required]);
    }

    placeOfSignControl?.updateValueAndValidity();
    manufacturerCompanyNameControl?.updateValueAndValidity();
    manufacturerAddressControl?.updateValueAndValidity();
    manufacturerContactPersonControl?.updateValueAndValidity();
    manufacturerEmailAddressControl?.updateValueAndValidity();
    manufacturerVatNumberControl?.updateValueAndValidity();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['proposal'] && changes['proposal'].currentValue) {
      //    if (this.proposalProductsOptions.length > 0 && this.jurisdictionsOptions.length > 0 && this.proposalTypesOptions.length > 0) {
      const proposalStepsValue = this.findKeyByValue(this.proposalsSteps, this.proposal?.proposalStep?.proposalStepId);
      const proposalModelValue = this.findKeyByValue(this.proposalsModels, this.proposal?.templateFormat);
      const paymentCompanyValue = this.findKeyByValue(this.paymentCompany, this.proposal?.paymentCompany);
      const paymentMethodValue = this.findKeyByValue(this.paymentMethods, this.proposal?.bankAccount?.bankAccountId);




      const proposalTimeDate = this.proposal?.proposalTime ? new Date(this.proposal.proposalTime) : null;
      const sentTimeDate = this.proposal?.sentTime ? new Date(this.proposal.sentTime) : null;
      const reminderDate = this.proposal?.reminderDate ? new Date(this.proposal.reminderDate) : null;
      const acceptedTimeDate = this.proposal?.acceptedTime ? new Date(this.proposal.acceptedTime) : null;

      this.form.patchValue({
        date: proposalTimeDate && !Number.isNaN(proposalTimeDate.getTime())
          ? this.commonService.formatDateOnly(proposalTimeDate)
          : '',
        category: this.proposal?.request.requestCategories?.map((cat: any) => cat.name).join(', ') || '-',
        projectTime: this.formatTimeToHHMM(this.proposal?.projectTime),
        proposalSteps: proposalStepsValue,
        proposalModel: proposalModelValue,
        placeOfSign: this.proposal?.placeOfSign,
        manufacturerCompanyName: this.proposal?.manufacturerCompanyName,
        manufacturerAddress: this.proposal?.manufacturerAddress,
        manufacturerContactPerson: this.proposal?.manufacturerContactPerson,
        manufacturerEmailAddress: this.proposal?.manufacturerEmailAddress,
        manufacturerVatNumber: this.proposal?.manufacturerVatNumber,
        paymentCompany: paymentCompanyValue,
        payamentCondiction: this.proposal?.proposalPaymentConditions,
        paymentMethod: paymentMethodValue,
        targetCurrencyId: this.proposal?.targetCurrency?.currencyId ?? '',
        targetExchangeRate: this.proposal?.targetExchangeRate ?? '',
        sentOn: this.proposal?.sentTime ? true : false,
        dateSentOn: sentTimeDate && !Number.isNaN(sentTimeDate.getTime())
          ? this.commonService.formatDateOnly(sentTimeDate)
          : '',
        reminderDate: reminderDate && !Number.isNaN(reminderDate.getTime())
          ? this.commonService.formatDateTimeLocal(reminderDate)
          : '',
        checked: this.proposal?.acceptedTime ? true : false,
        dateChecked: acceptedTimeDate && !Number.isNaN(acceptedTimeDate.getTime())
          ? this.commonService.formatDateOnly(acceptedTimeDate)
          : '',
      }, { emitEvent: false });
      //   }
      this.applyBaseCurrencyExchangeRate(this.proposal?.targetCurrency?.currencyId);
      this.emitTargetConversion();
    }
  }

  readonlyStep() {
    if (this.proposal?.status === 'Accepted / Not Accepteds' || this.proposal?.status === 'Pending Client') {
      return true;
    } else {
      return false;
    }
  }

  /**
   * *******************
   * Submit form methods
   * *******************
   */
  showAlertInfo(type: string, message: string) {
    this.alertInfo = {
      show: true,
      type,
      message
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      this.alertInfo.show = false;
    }, 3000);
  }
  updateValidationErrors(validationErrors: any) {
    this.formService.resetErrors(this.form);
    this.formService.setValidationErrors(this.form, validationErrors);
  }
  submitFormSuccess(response: any) {
    // this.alertNotificationService.success('Operation completed successfully')
    // this.showAlertInfo('success', 'Operation completed successfully');
    
    this.onSavedData.emit({
      success: true,
      response
    });
  }
  submitFormError(error: any) {

    if (error.error.error.description == 'ARGUMENT_VALIDATION_ERROR') {
      this.updateValidationErrors(error.error.error.validationErrors);
    }
    else if (error.error.error.description == 'USER_EMAIL_ALREADY_EXISTS') {
      this.updateValidationErrors([{ field: 'email', defaultMessage: error.error.error.message }]);
      this.showAlertInfo('danger', `Please fix the error: ${error.error.error.message}`);
    }
    else {
      this.showAlertInfo('danger', error.error.error.message);
    }

    this.onSavedData.emit({
      success: false,
      error
    });

  }
  submitForm(): void {
    this.paymentConditionsErrors = [];
    this.paymentConditionsInvalid = false;

    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      control?.markAsTouched();
      control?.updateValueAndValidity({ emitEvent: false });
    });
    
    const hasOtherFieldErrors = this.hasFormErrorsExceptPaymentConditions();
    
    if (this.paymentConditionsComp) {
      if (!this.paymentConditionsComp.isValid()) {
        this.paymentConditionsComp.markAllAsTouched();
        this.paymentConditionsErrors = this.paymentConditionsComp.getValidationErrors();
        this.paymentConditionsInvalid = true;
        const paymentConditionsControl = this.form.get('proposalPaymentConditions');
        paymentConditionsControl?.setErrors({ childInvalid: true });
        
        if (hasOtherFieldErrors) {
          const firstErrorField = this.getFirstErrorFieldName();
          this.showAlertInfo('danger', `Please fix the error in ${firstErrorField}`);
        }
        return;
      } else {
        const paymentConditionsControl = this.form.get('proposalPaymentConditions');
        if (paymentConditionsControl?.hasError('childInvalid')) {
          paymentConditionsControl.updateValueAndValidity();
        }
      }
    }
    
    if (hasOtherFieldErrors) {
      const firstErrorField = this.getFirstErrorFieldName();
      this.showAlertInfo('danger', `Please fix the error in ${firstErrorField}`);
      return;
    }
    
    if (this.form.valid) {
      const formValues = this.form.value;
      formValues.targetExchangeRate = this.form.get('targetExchangeRate')?.getRawValue();
      
      if (formValues.projectTime) {
        formValues.projectTime = this.formatHHMMToTime(formValues.projectTime);
      }

      if (formValues.reminderDate) {
        const reminderDate = new Date(formValues.reminderDate);
        formValues.reminderDate = Number.isNaN(reminderDate.getTime())
          ? ''
          : reminderDate.toISOString();
      }
      

      if (Array.isArray(formValues.proposalPaymentConditions)) {
        formValues.proposalPaymentConditions = formValues.proposalPaymentConditions.map((condition: any) => {
          const normalized: any = {
            percent: this.parseNumericValue(condition.percent ?? condition.rate),
            proposalPaymentConditionTypeId: this.parseNumericValue(
              condition.proposalPaymentConditionTypeId ?? (typeof condition.proposalPaymentConditionType === 'object'
                ? condition.proposalPaymentConditionType?.proposalPaymentConditionTypeId
                : condition.proposalPaymentConditionType)
            ),
            amount: this.parseNumericValue(condition.amount ?? condition.partial)
          };
          if (condition.proposalPaymentConditionId) {
            normalized.proposalPaymentConditionId = this.parseNumericValue(condition.proposalPaymentConditionId);
          }
          return normalized;
        });
      }
      
      const values = {
        ...formValues,
        bankAccountId: formValues.paymentMethod,
        proposalStepId: formValues.proposalSteps,
        templateFormat: formValues.proposalModel,
        tenantId: 1,
      };
      
      delete values.paymentMethod;
      delete values.proposalSteps;
      delete values.proposalModel;

      this.alertInfo.show = false;
      this.formService.resetErrors(this.form);
      this.submitFormSuccess(values);
    }
  }

  /**
    * *******************
    * Submit form methods
    * *******************
    */
  formatHHMMToTime(timeString: string): number {
    if (!timeString) return 0;
    
    if (typeof timeString === 'number') return timeString;
    
    const parts = timeString.split(':');
    if (parts.length !== 2) return 0;
    
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    
    if (isNaN(hours) || isNaN(minutes)) return 0;
    
    return hours * 100 + minutes;
  }

  formatTimeToHHMM = (timeValue: number | undefined): string => {
    if (!timeValue && timeValue !== 0) return '';
  const hours = Math.floor(timeValue / 100);
  const minutes = timeValue % 100;
  const hh = String(hours).padStart(2, '0');
  const mm = String(minutes).padStart(2, '0');

  return `${hh}:${mm}`;
  };

  getProposalDateInput(): FormControl { return this.getFormControl('date'); }
  getProposalCategoryInput(): FormControl { return this.getFormControl('category'); }
  getProposalProjectTimeInput(): FormControl { return this.getFormControl('projectTime'); }
  getProposalProposalStepsInput(): FormControl { return this.getFormControl('proposalSteps'); }
  getProposalProposalModelInput(): FormControl { return this.getFormControl('proposalModel'); }
  getProposalPlaceOfSignInput(): FormControl { return this.getFormControl('placeOfSign'); }
  getProposalPaymentCompanyInput(): FormControl { return this.getFormControl('paymentCompany'); }
  getProposalPayamentCondictionInput(): FormControl { return this.getFormControl('payamentCondiction'); }
  getProposalPaymentMethodInput(): FormControl { return this.getFormControl('paymentMethod'); }
  getProposalSentOnInput(): FormControl { return this.getFormControl('sentOn'); }
  getProposalDateSentOnInput(): FormControl { return this.getFormControl('dateSentOn'); }
  getProposalDateReminderInput(): FormControl { return this.getFormControl('reminderDate'); }
  getProposalCheckedInput(): FormControl { return this.getFormControl('checked'); }
  getProposalDateCheckedInput(): FormControl { return this.getFormControl('dateChecked'); }

  getManufactorerCompanyNameInput(): FormControl { return this.getFormControl('manufacturerCompanyName'); }
  getManufactorerAddressInput(): FormControl { return this.getFormControl('manufacturerAddress'); }
  getManufactorerContactPersonInput(): FormControl { return this.getFormControl('manufacturerContactPerson'); }
  getManufactorerEmailAddressInput(): FormControl { return this.getFormControl('manufacturerEmailAddress'); }
  getManufactorerVatNumberInput(): FormControl { return this.getFormControl('manufacturerVatNumber'); }

  getTargetExchangeRateInput(): FormControl { return this.getFormControl('targetExchangeRate'); }
  getTargetCurrencyIdInput(): FormControl { return this.getFormControl('targetCurrencyId'); }
  
  showPlaceOfSign(): boolean {
    const proposalModel = this.form.get('proposalModel')?.value;
    return proposalModel === 'SAME_MAN_APPL' || proposalModel === 'DIFF_MAN_APPL';
  }

  showManufactorerFields(): boolean {
    const proposalModel = this.form.get('proposalModel')?.value;
    return proposalModel === 'DIFF_MAN_APPL';
  }

  getPaymentCompanyOptions(): any[] {
    const proposalModel = this.form.get('proposalModel')?.value;
    if (proposalModel === 'SAME_MAN_APPL') {
      return this.paymentCompany.filter((option: any) => option.key === this.PAYMENT_COMPANY_APPLICANT_KEY);
    }
    return this.paymentCompany;
  }

  getFormControl(name: string): FormControl { return this.form.get(name) as FormControl; }
  
 
  updateProposalPaymentConditions(conditions: any[]): void {
    const control = this.form.get('proposalPaymentConditions');
    if (control && !this.arraysEqual(control.value, conditions)) {
      control.patchValue(conditions, { emitEvent: false });
    }
  }

  private arraysEqual(arr1: any[], arr2: any[]): boolean {
    if (!Array.isArray(arr1) || !Array.isArray(arr2)) return false;
    if (arr1.length !== arr2.length) return false;
    return arr1.every((item, index) => JSON.stringify(item) === JSON.stringify(arr2[index]));
  }
  
  private parseNumericValue(value: any): number | any {
    if (value === null || value === undefined || value === '') {
      return value;
    }
    
    const parsedValue = Number(value);
    return !isNaN(parsedValue) ? parsedValue : value;
  }

  private hasFormErrorsExceptPaymentConditions(): boolean {
    for (const key in this.form.controls) {
      if (key === 'proposalPaymentConditions') {
        continue;
      }
      
      const control = this.form.get(key);
      if (control && control.invalid && control.touched) {
        return true;
      }
    }
    return false;
  }
  private getFirstErrorFieldName(): string {
    for (const key in this.form.controls) {
      if (key === 'proposalPaymentConditions') {
        continue;
      }
      
      const control = this.form.get(key);
      if (control && control.invalid) {
        return this.formatFieldName(key);
      }
    }
    return 'form field';
  }

  private formatFieldName(fieldName: string): string {
    return fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }
}
